import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { errorMessage } from './errors'
import { http } from './api'
import type {
  Beneficiary,
  CreateRecurringInput,
  IndexationPreview,
  PersonalForecast,
  RecurringComment,
  RecurringDetail,
  RecurringOverview,
  RecurringUpdateInput,
} from './recurring-types'
export type { CreateRecurringInput, RecurringUpdateInput } from './recurring-types'

export interface RecurringApi {
  overview(): Promise<RecurringOverview>
  personalForecast(months?: number, personId?: string): Promise<PersonalForecast>
  get(id: string): Promise<RecurringDetail>
  create(input: CreateRecurringInput): Promise<RecurringDetail>
  update(id: string, input: RecurringUpdateInput): Promise<RecurringDetail>
  delete(id: string): Promise<void>
  generateNow(id: string): Promise<{ invoiceId: string }>
  setActive(id: string, active: boolean): Promise<RecurringDetail>
  indexationPreview(id: string, percent: string): Promise<IndexationPreview>
  indexationApply(id: string, percent: string): Promise<RecurringDetail>
  addComment(id: string, text: string): Promise<RecurringComment>
  setCommentResolved(id: string, commentId: string, resolved: boolean): Promise<RecurringComment>
  deleteComment(id: string, commentId: string): Promise<void>
  beneficiaries(): Promise<Beneficiary[]>
}

class RealRecurringApi implements RecurringApi {
  overview() {
    return http.get<RecurringOverview>('/api/recurring')
  }
  personalForecast(months = 12, personId?: string) {
    return http.get<PersonalForecast>('/api/recurring/forecast', { months, personId })
  }
  get(id: string) {
    return http.get<RecurringDetail>(`/api/recurring/${id}`)
  }
  create(input: CreateRecurringInput) {
    return http.post<RecurringDetail>('/api/recurring', input)
  }
  update(id: string, input: RecurringUpdateInput) {
    return http.patch<RecurringDetail>(`/api/recurring/${id}`, input)
  }
  delete(id: string) {
    return http.del<void>(`/api/recurring/${id}`)
  }
  generateNow(id: string) {
    return http.post<{ invoiceId: string }>(`/api/recurring/${id}/generate-now`)
  }
  setActive(id: string, active: boolean) {
    return http.patch<RecurringDetail>(`/api/recurring/${id}`, { active })
  }
  indexationPreview(id: string, percent: string) {
    return http.post<IndexationPreview>(`/api/recurring/${id}/indexation/preview`, { percent })
  }
  indexationApply(id: string, percent: string) {
    return http.post<RecurringDetail>(`/api/recurring/${id}/indexation/apply`, { percent })
  }
  addComment(id: string, text: string) {
    return http.post<RecurringComment>(`/api/recurring/${id}/comments`, { text })
  }
  setCommentResolved(id: string, commentId: string, resolved: boolean) {
    return http.patch<RecurringComment>(`/api/recurring/${id}/comments/${commentId}`, { resolved })
  }
  deleteComment(id: string, commentId: string) {
    return http.del<void>(`/api/recurring/${id}/comments/${commentId}`)
  }
  async beneficiaries() {
    const [persons, parties] = await Promise.all([
      http.get<{ persons: Array<{ id: string; displayName: string; active: boolean }> }>('/api/persons'),
      http.get<{ externalParties: Array<{ id: string; name: string; active: boolean }> }>('/api/external-parties'),
    ])
    return [
      ...persons.persons.filter((p) => p.active).map((p): Beneficiary => ({ id: p.id, label: p.displayName, kind: 'person' })),
      ...parties.externalParties.filter((p) => p.active).map((p): Beneficiary => ({ id: p.id, label: p.name, kind: 'external' })),
    ]
  }
}

export const recurringApi: RecurringApi = new RealRecurringApi()

export const recurringKeys = {
  overview: ['recurring', 'overview'] as const,
  personalForecast: (months: number, personId?: string) => ['recurring', 'forecast', 'personal', months, personId ?? 'self'] as const,
  detail: (id: string) => ['recurring', 'detail', id] as const,
  beneficiaries: ['recurring', 'beneficiaries'] as const,
}

export function useBeneficiaries() {
  return useQuery({ queryKey: recurringKeys.beneficiaries, queryFn: () => recurringApi.beneficiaries(), staleTime: 5 * 60_000 })
}

export function useRecurringOverview() {
  return useQuery({ queryKey: recurringKeys.overview, queryFn: () => recurringApi.overview() })
}

/** Het aandeel in de prognose van jezelf of, met personId, van een collega. */
export function useRecurringPersonalForecast(months = 12, personId?: string, enabled = true) {
  return useQuery({
    queryKey: recurringKeys.personalForecast(months, personId),
    queryFn: () => recurringApi.personalForecast(months, personId),
    enabled,
  })
}

export function useRecurringDetail(id: string) {
  return useQuery({ queryKey: recurringKeys.detail(id), queryFn: () => recurringApi.get(id) })
}

export function useRecurringActions(id: string) {
  const queryClient = useQueryClient()
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: recurringKeys.overview })
    queryClient.invalidateQueries({ queryKey: recurringKeys.detail(id) })
  }
  const generateNow = useMutation({
    mutationFn: () => recurringApi.generateNow(id),
    onSuccess: () => { invalidate(); toast.success('Concept gegenereerd') },
    onError: (e) => toast.error(errorMessage(e, 'Genereren mislukt')),
  })
  const setActive = useMutation({
    mutationFn: (active: boolean) => recurringApi.setActive(id, active),
    onSuccess: (d) => { queryClient.setQueryData(recurringKeys.detail(id), d); invalidate() },
    onError: (e) => toast.error(errorMessage(e, 'Wijzigen mislukt')),
  })
  const indexationApply = useMutation({
    mutationFn: (percent: string) => recurringApi.indexationApply(id, percent),
    onSuccess: (d) => { queryClient.setQueryData(recurringKeys.detail(id), d); invalidate(); toast.success('Indexatie toegepast') },
    onError: (e) => toast.error(errorMessage(e, 'Toepassen mislukt')),
  })
  const update = useMutation({
    mutationFn: (input: RecurringUpdateInput) => recurringApi.update(id, input),
    onSuccess: (d) => { queryClient.setQueryData(recurringKeys.detail(id), d); invalidate(); toast.success('Template opgeslagen') },
    onError: (e) => toast.error(errorMessage(e, 'Opslaan mislukt')),
  })
  const addComment = useMutation({
    mutationFn: (text: string) => recurringApi.addComment(id, text),
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(errorMessage(e, 'Opmerking plaatsen mislukt')),
  })
  const setCommentResolved = useMutation({
    mutationFn: ({ commentId, resolved }: { commentId: string; resolved: boolean }) =>
      recurringApi.setCommentResolved(id, commentId, resolved),
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(errorMessage(e, 'Wijzigen mislukt')),
  })
  const deleteComment = useMutation({
    mutationFn: (commentId: string) => recurringApi.deleteComment(id, commentId),
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(errorMessage(e, 'Verwijderen mislukt')),
  })
  return { generateNow, setActive, indexationApply, update, addComment, setCommentResolved, deleteComment }
}

export function useCreateRecurring() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateRecurringInput) => recurringApi.create(input),
    onSuccess: (detail) => {
      queryClient.setQueryData(recurringKeys.detail(detail.id), detail)
      queryClient.invalidateQueries({ queryKey: recurringKeys.overview })
      toast.success('Template aangemaakt')
    },
    onError: (e) => toast.error(errorMessage(e, 'Aanmaken mislukt')),
  })
}

export function useDeleteRecurring() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => recurringApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recurringKeys.overview })
      toast.success('Reeks verwijderd')
    },
    onError: (e) => toast.error(errorMessage(e, 'Verwijderen mislukt')),
  })
}

export function useIndexationPreview(id: string) {
  return useMutation({
    mutationFn: (percent: string) => recurringApi.indexationPreview(id, percent),
    onError: (e) => toast.error(errorMessage(e, 'Preview mislukt')),
  })
}
